import { useState, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { v4 as uuid } from 'uuid';
import Form, { FormContent, FormActions, FormSubmit } from '@/components/Form';
import UPDATE_ORDER_MUTATION from '@/graphql/mutations/updateOrder.graphql';
import { notifyError } from '@/components/Toaster';
import runPayPalPaymentWorkflow from '@/components/PayPalPaymentWorkflow';
import runPaymentezPaymentWorkflow from '@/components/PaymentezPaymentWorkflow';
import LoadingIndicator from '@/components/LoadingIndicator';
import clsx from 'clsx';
import PaymentezLogo from '@/public/payments/paymentez.png';
import PayPalLogo from '@/public/payments/paypal.svg';

const CheckoutPaymentContext = createContext();

export const PAYMENT_METHOD_PAYMENTEZ = 'paymentez';
export const PAYMENT_METHOD_PAYPAL = 'paypal';

const paymentMethods = [
  {
    id: PAYMENT_METHOD_PAYMENTEZ,
    name: 'Paymentez',
    logo: PaymentezLogo,
    logoSize: 'w-36',
    description:
      'Realiza tu pago en línea usando tu tarjeta de crédito de forma segura, fácil y rápida.',
    icon: (
      <svg className="w-5 max-w-full h-auto fill-current" viewBox="0 0 20 20">
        <path d="M14.781,14.347h1.738c0.24,0,0.436-0.194,0.436-0.435v-1.739c0-0.239-0.195-0.435-0.436-0.435h-1.738c-0.239,0-0.435,0.195-0.435,0.435v1.739C14.347,14.152,14.542,14.347,14.781,14.347 M18.693,3.045H1.307c-0.48,0-0.869,0.39-0.869,0.869v12.17c0,0.479,0.389,0.869,0.869,0.869h17.387c0.479,0,0.869-0.39,0.869-0.869V3.915C19.562,3.435,19.173,3.045,18.693,3.045 M18.693,16.085H1.307V9.13h17.387V16.085z M18.693,5.653H1.307V3.915h17.387V5.653zM3.48,12.608h7.824c0.24,0,0.435-0.195,0.435-0.436c0-0.239-0.194-0.435-0.435-0.435H3.48c-0.24,0-0.435,0.195-0.435,0.435C3.045,12.413,3.24,12.608,3.48,12.608 M3.48,14.347h6.085c0.24,0,0.435-0.194,0.435-0.435s-0.195-0.435-0.435-0.435H3.48c-0.24,0-0.435,0.194-0.435,0.435S3.24,14.347,3.48,14.347" />
      </svg>
    ),
  },
  {
    id: PAYMENT_METHOD_PAYPAL,
    name: 'PayPal',
    logo: PayPalLogo,
    logoSize: 'w-24',
    description:
      'Puedes pagar con tu tarjeta de crédito o débito incluso si no tienes una cuenta de PayPal.',
    icon: (
      <svg
        className="max-w-full h-auto fill-current"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
      >
        <path fill="none" d="M0 0h24v24H0z" />
        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.794.68l-.04.22-.63 3.993-.032.17a.804.804 0 0 1-.794.679H7.72a.483.483 0 0 1-.477-.558L7.418 21h1.518l.95-6.02h1.385c4.678 0 7.75-2.203 8.796-6.502zm-2.96-5.09c.762.868.983 1.81.752 3.285-.019.123-.04.24-.062.36-.735 3.773-3.089 5.446-6.956 5.446H8.957c-.63 0-1.174.414-1.354 1.002l-.014-.002-.93 5.894H3.121a.051.051 0 0 1-.05-.06l2.598-16.51A.95.95 0 0 1 6.607 2h5.976c2.183 0 3.716.469 4.523 1.388z" />
      </svg>
    ),
  },
];

const getPaymentMethodTitle = (paymentMethod) => {
  let methodTitle = '__UNDEFINED__';
  const method = paymentMethods.find((method) => method.id === paymentMethod);
  if (method) {
    methodTitle = method.name;
  }
  return methodTitle;
};

const paymentValidationSchema = Yup.object().shape({
  paymentMethod: Yup.string()
    .oneOf([PAYMENT_METHOD_PAYMENTEZ, PAYMENT_METHOD_PAYPAL])
    .required(),
});

const getPaymentCheckoutData = ({ user, order }) => {
  const { customer: userCustomer, ...restUser } = user;
  const { billing: orderCustomer, ...restOrder } = order;

  const customer = { ...userCustomer, ...orderCustomer };

  return {
    user: restUser,
    customer,
    order: { ...restOrder, billing: orderCustomer, customer },
  };
};

export function CheckoutPaymentContextProvider({
  user,
  order,
  router,
  apolloClient,
  isActive,
  completeCustomerOrder,
  children,
}) {
  const [updatingOrderWithPaymentDetails, setUpdatingOrder] = useState(false);

  const onPaymentAcceptedCallback = useCallback(
    async ({ transaction, paymentMethod }) => {
      setUpdatingOrder(true);

      const metaData = Object.keys(transaction)
        .map((key) => {
          let value = transaction[key];
          if (typeof value === 'object') {
            value = JSON.stringify(value);
          }
          return { key: `${paymentMethod}_${key}`, value };
        })
        .filter((meta) => meta !== null);

      // TODO: check if we receive an error here
      await apolloClient.mutate({
        mutation: UPDATE_ORDER_MUTATION,
        variables: {
          input: {
            clientMutationId: uuid(),
            orderId: order.orderId,
            transactionId: transaction.id,
            customerId: user.customer.customerId,
            paymentMethod,
            paymentMethodTitle: getPaymentMethodTitle(paymentMethod),
            status: 'COMPLETED',
            isPaid: true,
            metaData,
          },
        },
      });

      completeCustomerOrder({
        onCompleted: () => router.push(`/pago/exito/${order.id}`),
      });
    },
    [user, apolloClient, completeCustomerOrder, order, router]
  );

  const onPaymentRejectedCallback = useCallback((err) => {
    console.log('onPaymentRejectedCallback()', err);
  }, []);

  const onPaymentCanceledCallback = useCallback(() => {
    console.log('onPaymentCanceledCallback()');
  }, []);

  const executePaymentWorkflow = useCallback(
    async ({ paymentMethod, setSubmitting }) => {
      setSubmitting(true);

      const paymentWorkflowProps = {
        checkout: getPaymentCheckoutData({
          user,
          order,
        }),
        onPaymentAccepted: (transaction) =>
          onPaymentAcceptedCallback({ transaction, paymentMethod }),
        onPaymentRejected: onPaymentRejectedCallback,
        onPaymentCanceled: onPaymentCanceledCallback,
      };

      switch (paymentMethod) {
        case PAYMENT_METHOD_PAYMENTEZ:
          runPaymentezPaymentWorkflow(paymentWorkflowProps);
          break;
        case PAYMENT_METHOD_PAYPAL:
          runPayPalPaymentWorkflow(paymentWorkflowProps);
          break;
        default:
          notifyError(
            'Ha seleccionado un método de pago indefinido. Por favor, ' +
              'inténtelo nuevamente seleccionando uno de los métodos de ' +
              'pago disponibles que aparecen en la lista.'
          );
          setSubmitting(false);
          break;
      }
    },
    [
      user,
      order,
      onPaymentAcceptedCallback,
      onPaymentRejectedCallback,
      onPaymentCanceledCallback,
    ]
  );

  return (
    <div
      className={clsx(
        'relative w-full',
        updatingOrderWithPaymentDetails && 'pointer-events-none'
      )}
    >
      {updatingOrderWithPaymentDetails && (
        <div className="absolute z-50 inset-0 pointer-events-none flex items-center justify-center">
          <div className="relative z-20 flex flex-col items-center space-y-4">
            <div>
              <LoadingIndicator size="w-12" />
            </div>
            <div className="text-center px-20">
              Actualizando el estado del pedido. Por favor, no cierre esta
              ventana.
            </div>
          </div>
          <div className="absolute inset-0 z-0 bg-white opacity-95" />
          <div className="absolute inset-0 z-0 bg-white opacity-50" />
        </div>
      )}
      <Form
        initialValues={{
          paymentMethod: order.paymentMethod || '',
        }}
        className="relative z-10"
        validationSchema={paymentValidationSchema}
        onSubmit={(values, { setSubmitting }) =>
          executePaymentWorkflow({
            paymentMethod: values.paymentMethod,
            setSubmitting,
          })
        }
        validateOnMount={true}
      >
        {({ values, setFieldValue, isValid, isSubmitting }) => {
          const value = {
            paymentMethods,
            selectedPaymentMethod: values.paymentMethod,
            selectPaymentMethod: (paymentMethodId) =>
              setFieldValue('paymentMethod', paymentMethodId, true),
            isPayingCurrentOrder: isSubmitting,
          };

          return (
            <CheckoutPaymentContext.Provider value={value}>
              <FormContent>
                {children}
                {isActive && (
                  <FormActions>
                    <div className="flex justify-end flex-1">
                      <FormSubmit
                        disabled={!isValid}
                        isSubmitting={isSubmitting}
                      >
                        Realizar pago seguro
                      </FormSubmit>
                    </div>
                  </FormActions>
                )}
              </FormContent>
            </CheckoutPaymentContext.Provider>
          );
        }}
      </Form>
    </div>
  );
}

CheckoutPaymentContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useCheckoutPayment() {
  const context = useContext(CheckoutPaymentContext);
  if (context === undefined) {
    throw new Error(
      'useCheckoutPayment() must be used within a <CheckoutPaymentContext />'
    );
  }
  return context;
}
